/**
 * ForceReacceptanceModal — blokujący modal wymuszający ponowną akceptację
 * dokumentów prawnych po opublikowaniu nowej wersji z `forceReacceptance=true`.
 *
 * Pobiera listę „pending" dokumentów po zalogowaniu użytkownika. Jeśli lista
 * niepusta — pokazuje modal z podglądem treści + checkboxami akceptacji.
 * Modal blokuje całą aplikację dopóki user nie zaakceptuje wszystkich.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Scale, Check, Loader2, FileText, Shield, Cookie } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  regulamin: FileText,
  privacy:   Shield,
  cookies:   Cookie,
};

const ForceReacceptanceModal = ({ user }) => {
  const [pending, setPending] = useState([]);
  const [accepted, setAccepted] = useState({});
  const [expanded, setExpanded] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!user?.username && !user?.id) return;
    axios.get(`${API}/api/legal/pending-acceptance`, {
      params: { userId: user.id || user.username, userEmail: user.email || null },
    }).then((r) => {
      setPending(r.data.pending || []);
      setChecked(true);
    }).catch(() => setChecked(true));
  }, [user?.username, user?.id, user?.email]);

  if (!checked || pending.length === 0) return null;

  const allAccepted = pending.every((d) => accepted[d.type]);

  const submit = async () => {
    if (!allAccepted) return toast.error('Musisz zaakceptować wszystkie dokumenty');
    setSubmitting(true);
    try {
      const docVersions = {};
      pending.forEach((d) => { docVersions[d.type] = d.version; });
      await axios.post(`${API}/api/legal/consents`, {
        context: 'reacceptance',
        documentTypes: pending.map((d) => d.type),
        documentVersions: docVersions,
        userId: user.id || user.username,
        userEmail: user.email || null,
      });
      toast.success('Dziękujemy za akceptację');
      setPending([]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Błąd zapisu');
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 12000,
      background: 'rgba(6, 11, 22, 0.94)', backdropFilter: 'blur(12px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }} data-testid="force-reacceptance-modal">
      <div style={{
        width: 'min(96vw, 720px)', maxHeight: '92vh',
        background: 'linear-gradient(180deg, #0f172a 0%, #060b16 100%)',
        border: '1px solid rgba(212,175,55,0.32)', borderRadius: 20,
        boxShadow: '0 40px 100px -20px rgba(0,0,0,0.8), 0 0 60px -20px rgba(212,175,55,0.3)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Gold header */}
        <div style={{ height: 4, background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />

        <div style={{ padding: '26px 28px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 12,
            padding: '6px 14px 6px 6px', borderRadius: 999,
            background: 'rgba(212,175,55,0.10)', border: '1px solid rgba(212,175,55,0.28)',
            marginBottom: 14,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: 999,
              background: 'linear-gradient(135deg,#d4af37,#b8860b)',
              display: 'grid', placeItems: 'center', color: '#0b1220',
            }}>
              <Scale size={16} strokeWidth={2.2} />
            </div>
            <span style={{ fontSize: 11, letterSpacing: 2, color: '#d4af37', fontWeight: 800, textTransform: 'uppercase' }}>
              Aktualizacja regulaminów
            </span>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: '#f8fafc', letterSpacing: '-0.01em' }}>
            Zaktualizowaliśmy nasze dokumenty prawne
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 13, color: '#94a3b8', lineHeight: 1.55 }}>
            Aby móc dalej korzystać z aplikacji, prosimy o zapoznanie się z {pending.length === 1 ? 'aktualnym dokumentem' : `${pending.length} aktualnymi dokumentami`} i wyrażenie akceptacji.
          </p>
        </div>

        <div style={{ padding: '18px 28px', overflowY: 'auto', flex: 1 }}>
          {pending.map((doc) => {
            const Icon = ICON_MAP[doc.type] || FileText;
            const isExpanded = !!expanded[doc.type];
            const isAccepted = !!accepted[doc.type];
            return (
              <div key={doc.type} style={{
                marginBottom: 14, borderRadius: 14,
                background: isAccepted ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${isAccepted ? 'rgba(34,197,94,0.28)' : 'rgba(255,255,255,0.08)'}`,
                overflow: 'hidden',
              }}>
                <div style={{
                  padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: 'rgba(212,175,55,0.14)', color: '#d4af37',
                    display: 'grid', placeItems: 'center', flexShrink: 0,
                  }}>
                    <Icon size={18} strokeWidth={1.8} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, color: '#f8fafc', fontSize: 14 }}>{doc.title}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Wersja {doc.version} · {new Date(doc.publishedAt).toLocaleDateString('pl-PL')}
                      {doc.changeNote && ` · ${doc.changeNote}`}
                    </div>
                  </div>
                  <button
                    onClick={() => setExpanded((e) => ({ ...e, [doc.type]: !isExpanded }))}
                    style={{
                      padding: '6px 12px', borderRadius: 999,
                      background: 'transparent', color: '#d4af37',
                      border: '1px solid rgba(212,175,55,0.35)', cursor: 'pointer',
                      fontSize: 11, fontWeight: 700,
                    }}
                    data-testid={`reacceptance-toggle-${doc.type}`}
                  >
                    {isExpanded ? 'Zwiń' : 'Przeczytaj'}
                  </button>
                </div>

                {isExpanded && (
                  <div style={{
                    padding: '2px 16px 14px',
                    fontSize: 12, color: '#cbd5e1', lineHeight: 1.55,
                    maxHeight: 240, overflowY: 'auto',
                    whiteSpace: 'pre-wrap', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    background: 'rgba(0,0,0,0.2)', borderTop: '1px solid rgba(255,255,255,0.05)',
                  }}>
                    {doc.content?.slice(0, 4000)}
                    {doc.content?.length > 4000 && '\n\n… (pełna treść dostępna po akceptacji na stronie dokumentu)'}
                  </div>
                )}

                <label style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '12px 16px',
                  background: 'rgba(0,0,0,0.15)',
                  borderTop: '1px solid rgba(255,255,255,0.05)',
                  cursor: 'pointer', userSelect: 'none',
                }} data-testid={`reacceptance-accept-${doc.type}`}>
                  <input type="checkbox" checked={isAccepted}
                    onChange={(e) => setAccepted((a) => ({ ...a, [doc.type]: e.target.checked }))}
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }} />
                  <div style={{
                    flexShrink: 0, width: 22, height: 22, borderRadius: 6,
                    border: `1.5px solid ${isAccepted ? '#22c55e' : 'rgba(255,255,255,0.2)'}`,
                    background: isAccepted ? '#22c55e' : 'rgba(6,11,22,0.6)',
                    color: isAccepted ? '#0b1220' : 'transparent',
                    display: 'grid', placeItems: 'center',
                    transition: 'all 0.15s ease',
                  }}>
                    <Check size={14} strokeWidth={3} />
                  </div>
                  <span style={{ fontSize: 13, color: '#f8fafc', fontWeight: 600 }}>
                    Zapoznałem/-am się z treścią i akceptuję
                  </span>
                </label>
              </div>
            );
          })}
        </div>

        <div style={{ padding: '18px 28px 22px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={submit}
            disabled={!allAccepted || submitting}
            data-testid="reacceptance-submit"
            style={{
              padding: '13px 28px', borderRadius: 999,
              background: allAccepted
                ? 'linear-gradient(135deg,#d4af37,#b8860b)'
                : 'rgba(255,255,255,0.06)',
              color: allAccepted ? '#0b1220' : '#64748b',
              border: 'none', cursor: allAccepted && !submitting ? 'pointer' : 'not-allowed',
              fontWeight: 800, fontSize: 14,
              boxShadow: allAccepted ? '0 12px 30px -12px rgba(212,175,55,0.5)' : 'none',
              display: 'inline-flex', alignItems: 'center', gap: 8,
              transition: 'all 0.2s ease',
            }}
          >
            {submitting
              ? <><Loader2 size={16} className="frm-spin" /> Zapisywanie…</>
              : <><Check size={16} /> Akceptuję i kontynuuję</>}
          </button>
        </div>
      </div>
      <style>{`.frm-spin{animation:frm-r 0.8s linear infinite}@keyframes frm-r{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default ForceReacceptanceModal;
